<template>
  <div class="data-view-wrapper" @click="handleWrapperClick">
    <div class="upload-overlay" v-if="isUploading">
      <div class="upload-spinner"></div>
      <span class="upload-overlay-text">上传中...</span>
    </div>
    <p class="update-time">上次更新时间：{{ lastUpdateTime }}</p>

    <div class="search-bar">
      <button class="clear-all-btn" @click="$emit('clear-all-filters')" :disabled="isFilterLoading">
        <LoadingSpinner v-if="isFilterLoading" :visible="true" :inline="true" text="清除中..." />
        <span v-else>清空全部筛选</span>
      </button>
      <input
        type="text"
        v-model="localSearchKeyword"
        placeholder="按关键词搜索..."
        class="search-input"
        @input="onSearchInput"
      />
    </div>

    <div class="table-wrapper">
      <ChartSpinner :visible="isLoading" :show-watermark="false" />
      <template v-if="!isLoading">
        <div v-if="pagedData.length > 0" class="table-shell">
          <div class="table-scroll-body">
            <table class="data-table">
              <thead>
                <tr>
                  <th>序号</th>
                  <th v-for="colKey in displayHeaders" :key="colKey">
                    <div class="th-wrapper">
                      {{ getColumnLabel(colKey) }}
                      <template v-if="isColumnFilterable(colKey)">
                        <span class="filter-icon" @click.stop="toggleFilterDropdown(colKey)" :data-filter-icon="colKey">⏷</span>
                        <div v-show="showFilterDropdown[colKey]" class="filter-dropdown" :data-dropdown="colKey" @click.stop>
                          <input class="filter-search-input" v-model="filterSearch[colKey]" placeholder="搜索该列..." />
                          <div class="filter-options">
                            <label v-for="val in getFilteredOptions(colKey)" :key="val">
                              <input type="checkbox" :value="val" v-model="pendingFilters[colKey]" />
                              {{ val }}
                            </label>
                          </div>
                          <div class="filter-footer">
                            <button @click="clearColumnFilter(colKey)">清空筛选</button>
                            <button @click="confirmFilter(colKey)" :disabled="isFilterLoading">
                              <LoadingSpinner v-if="isFilterLoading" :visible="true" :inline="true" text="筛选中..." />
                              <span v-else>确定</span>
                            </button>
                          </div>
                        </div>
                      </template>
                    </div>
                  </th>
                  <th class="modification-log-header" v-if="!skipAuditLog">更新日志</th>
                </tr>
              </thead>

              <tbody v-for="(group, groupIndex) in pagedData" :key="group.group_id || groupIndex">
                <tr
                  v-for="row in group.row_group"
                  :key="row.id"
                  @contextmenu.prevent="showContextMenu($event, row)"
                >
                  <td style="text-align: center;">{{ (currentPage - 1) * pageSize + groupIndex + 1 }}</td>

                  <td
                    v-for="(colName, colIndex) in displayHeaders"
                    :key="`${colName}-${colIndex}`"
                    :class="{
                      'text-right': isNumericColumn(colName),
                      'cell-editing': isEditingThisCell(row.id, colName),
                      'cell-editable': !isReadonlyCol(colName)
                    }"
                    @dblclick.stop="startEdit(row, colName)"
                  >
                    <input
                      v-if="isEditingThisCell(row.id, colName)"
                      class="cell-input"
                      v-model="editingValue"
                      @keyup.enter="commitEdit(row, colName)"
                      @keyup.esc="cancelEdit"
                      @blur="commitEdit(row, colName)"
                      ref="cellInput"
                    />
                    <span v-else v-html="applyCellFormatting(row[colName], colName)"></span>
                  </td>

                  <td v-if="!skipAuditLog" class="modification-log-cell">
                    <div class="log-content">
                      <template v-if="!hasLogContent(row.modification_log)">
                        <div class="log-empty">暂无修改记录</div>
                      </template>
                      <template v-else-if="!expandedLogs[row.id]">
                        <div class="log-preview">{{ row.modification_log }}</div>
                        <div class="log-actions">
                          <span class="show-more-btn" @click.stop="expandLog(row.id)">展开</span>
                        </div>
                      </template>
                      <template v-else>
                        <div class="log-full">{{ row.modification_log }}</div>
                        <div class="log-actions">
                          <span class="show-less-btn" @click.stop="collapseLog(row.id)">收起</span>
                        </div>
                      </template>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <p v-else class="no-data">暂无数据或结果为空</p>
      </template>
    </div>

    <PaginationControls
      v-if="totalPages > 0 && !isLoading && pagedData.length > 0"
      :current-page="currentPage"
      :total-pages="totalPages"
      :page-size="pageSize"
      :is-page-loading="isPageLoading"
      :is-prev-loading="isPrevLoading"
      :is-next-loading="isNextLoading"
      :is-jump-loading="isJumpLoading"
      @page-changed="(page) => $emit('page-changed', page)"
    />

    <div class="export-controls">
      <label class="upload-btn" :class="{ 'is-disabled': isUploading }" v-if="isUploadVisible">
        <span>{{ isUploading ? '上传中...' : '上传数据' }}</span>
        <input
          type="file"
          accept=".xlsx"
          @change="onFileSelect"
          hidden
          ref="fileInput"
          :disabled="isUploading"
        />
      </label>
      <button class="export-btn" @click="$emit('export-excel')" :disabled="isExportLoading">
        <LoadingSpinner v-if="isExportLoading" :visible="true" :inline="true" text="导出中..." />
        <span v-else>导出 Excel</span>
      </button>
    </div>

    <!-- Right-click context menu -->
    <div
      v-if="contextMenu.visible"
      class="context-menu"
      :style="{ top: contextMenu.y + 'px', left: contextMenu.x + 'px' }"
      @click.stop
    >
      <div v-if="!contextMenu.confirming" class="context-menu-item ctx-delete" @click="contextMenu.confirming = true">
        <span class="ctx-icon">🗑</span>
        <span>删除此行</span>
      </div>
      <div v-else class="context-menu-confirm">
        <p class="ctx-confirm-text">确认删除？</p>
        <div class="ctx-confirm-btns">
          <button class="ctx-btn-confirm" @click="confirmDelete">确认</button>
          <button class="ctx-btn-cancel" @click="closeContextMenu">取消</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ChartSpinner from '@/components/common/ChartSpinner.vue';
import PaginationControls from '@/components/common/PaginationControls.vue';
import LoadingSpinner from '@/components/common/LoadingSpinner.vue';
import { formatToChineseYearMonth, formatToYYYYMMDD, formatNumber, formatPercentage, formatToDateTimeSec } from '@/utils/formatters.js';

const READONLY_COLS = new Set(['updated_at', 'data_status', 'published_at', 'version_number']);
const SKIP_FROM_DISPLAY = new Set(['status', 'modification_log', 'published_by']);

export default {
  name: 'DataManager',
  components: { ChartSpinner, PaginationControls, LoadingSpinner },
  props: {
    isLoading: Boolean,
    pagedData: { type: Array, default: () => [] },
    headers: { type: Array, default: () => [] },
    uniqueColumnValues: { type: Object, default: () => ({}) },
    lastUpdateTime: String,
    currentPage: Number,
    pageSize: Number,
    totalRows: Number,
    isUploadVisible: Boolean,
    activeFilters: { type: Object, default: () => ({}) },
    searchKeyword: String,
    skipAuditLog: { type: Boolean, default: false },
    isFilterLoading: { type: Boolean, default: false },
    isExportLoading: { type: Boolean, default: false },
    isPageLoading: { type: Boolean, default: false },
    isPrevLoading: { type: Boolean, default: false },
    isNextLoading: { type: Boolean, default: false },
    isJumpLoading: { type: Boolean, default: false }
  },
  emits: [
    'clear-all-filters', 'search-input', 'apply-filters', 'page-changed',
    'upload-excel', 'export-excel', 'cell-updated', 'row-deleted'
  ],
  data() {
    return {
      localSearchKeyword: this.searchKeyword || '',
      searchDebounceTimer: null,
      showFilterDropdown: {},
      pendingFilters: {},
      filterSearch: {},
      isUploading: false,
      expandedLogs: {},
      editingCell: { rowId: null, colName: null },
      editingValue: '',
      originalEditValue: null,
      contextMenu: { visible: false, x: 0, y: 0, row: null, confirming: false },
      columnFormattersConfig: {
        'register_date': formatToYYYYMMDD,
        'updated_at': formatToDateTimeSec,
        'month_time': formatToChineseYearMonth,
        '入股时间': formatToChineseYearMonth,
        '日期': formatToChineseYearMonth,
        '时间': formatToChineseYearMonth,
        '数据资源入表总额（万元）': (val) => formatNumber(val, 2),
        '市值（亿元）': (val) => formatNumber(val, 2),
        '无形资产-数据资源入表金额（万元）': (val) => formatNumber(val, 2),
        '开发支出-数据资源入表金额（万元）': (val) => formatNumber(val, 2),
        '存货-数据资源入表金额（万元）': (val) => formatNumber(val, 2),
        'book_value': (val) => formatNumber(val, 2),
        'assess_value': (val) => formatNumber(val, 2),
        'finance_value': (val) => formatNumber(val, 2),
        '融资金额（万元）': (val) => formatNumber(val, 2),
        '注册资本（万元）': (val) => formatNumber(val, 2),
        '数据资产占总资产比例': (val) => formatPercentage(val, 1),
        '股权占比': (val) => formatPercentage(val, 1)
      },
      numericColumns: new Set([
        '数据资源入表总额（万元）', '市值（亿元）', '无形资产-数据资源入表金额（万元）',
        '开发支出-数据资源入表金额（万元）', '存货-数据资源入表金额（万元）',
        'book_value', 'assess_value', 'finance_value',
        '融资金额（万元）', '注册资本（万元）', '数据资产占总资产比例', '股权占比'
      ])
    };
  },
  computed: {
    totalPages() {
      if (!this.totalRows || !this.pageSize) return 0;
      return Math.max(1, Math.ceil(this.totalRows / this.pageSize));
    },
    displayHeaders() {
      return this.headers.filter(h => !SKIP_FROM_DISPLAY.has(h));
    }
  },
  watch: {
    searchKeyword(newVal) {
      if (newVal !== this.localSearchKeyword) this.localSearchKeyword = newVal;
    }
  },
  methods: {
    // ── Column helpers ────────────────────────────────────────────
    getColumnLabel(colKey) {
      if (colKey === 'updated_at') return '更新时间';
      if (colKey === 'remark') return '备注';
      return colKey;
    },
    isNumericColumn(colName) { return this.numericColumns.has(colName); },
    isReadonlyCol(colName) { return READONLY_COLS.has(colName); },
    isColumnFilterable(columnKey) { return this.uniqueColumnValues[columnKey]?.length > 0; },
    applyCellFormatting(value, columnName) {
      const formatter = this.columnFormattersConfig[columnName];
      const formatted = typeof formatter === 'function' ? formatter(value) : (value ?? '');
      return this.highlight(String(formatted));
    },
    highlight(value) {
      if (!this.searchKeyword || !value) return value;
      const regex = new RegExp(`(${this.searchKeyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
      return value.replace(regex, '<mark>$1</mark>');
    },

    // ── Inline editing ────────────────────────────────────────────
    isEditingThisCell(rowId, colName) {
      return this.editingCell.rowId === rowId && this.editingCell.colName === colName;
    },
    startEdit(row, colName) {
      if (this.isReadonlyCol(colName)) return;
      this.closeAllDropdowns();
      this.closeContextMenu();
      const rawValue = row[colName];
      this.originalEditValue = rawValue;
      this.editingValue = rawValue ?? '';
      this.editingCell = { rowId: row.id, colName };
      this.$nextTick(() => {
        const ref = this.$refs.cellInput;
        const input = Array.isArray(ref) ? ref[0] : ref;
        if (input) { input.focus(); input.select(); }
      });
    },
    commitEdit(row, colName) {
      if (!this.isEditingThisCell(row.id, colName)) return;
      const newValue = this.editingValue;
      const oldValue = this.originalEditValue;
      this.editingCell = { rowId: null, colName: null };
      this.editingValue = '';
      this.originalEditValue = null;
      if (String(oldValue ?? '') !== String(newValue ?? '')) {
        this.$emit('cell-updated', { row, colName, oldValue, newValue: newValue === '' ? null : newValue });
      }
    },
    cancelEdit() {
      this.editingCell = { rowId: null, colName: null };
      this.editingValue = '';
      this.originalEditValue = null;
    },

    // ── Search & filter ───────────────────────────────────────────
    onSearchInput() {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.$emit('search-input', this.localSearchKeyword);
      }, 500);
    },
    toggleFilterDropdown(col) {
      const was = this.showFilterDropdown[col];
      this.closeAllDropdowns();
      this.showFilterDropdown = { ...this.showFilterDropdown, [col]: !was };
      if (!was) {
        this.pendingFilters = { ...this.pendingFilters, [col]: [...(this.activeFilters[col] || [])] };
        this.filterSearch = { ...this.filterSearch, [col]: '' };
      }
    },
    confirmFilter(col) {
      this.$emit('apply-filters', { column: col, values: this.pendingFilters[col] });
      this.closeAllDropdowns();
    },
    clearColumnFilter(col) {
      this.pendingFilters[col] = [];
      this.$emit('apply-filters', { column: col, values: [] });
      this.closeAllDropdowns();
    },
    closeAllDropdowns() { this.showFilterDropdown = {}; },
    getFilteredOptions(col) {
      const options = this.uniqueColumnValues[col] || [];
      const search = (this.filterSearch[col] || '').toLowerCase();
      return search ? options.filter(v => String(v).toLowerCase().includes(search)) : options;
    },

    // ── Context menu ──────────────────────────────────────────────
    showContextMenu(event, row) {
      this.cancelEdit();
      this.closeAllDropdowns();
      const W = 170, H = 96;
      const x = (event.clientX + W > window.innerWidth) ? window.innerWidth - W - 8 : event.clientX;
      const y = (event.clientY + H > window.innerHeight) ? window.innerHeight - H - 8 : event.clientY;
      this.contextMenu = { visible: true, x, y, row, confirming: false };
    },
    closeContextMenu() {
      this.contextMenu = { visible: false, x: 0, y: 0, row: null, confirming: false };
    },
    confirmDelete() {
      if (this.contextMenu.row) this.$emit('row-deleted', this.contextMenu.row);
      this.closeContextMenu();
    },
    handleWrapperClick() {
      this.closeContextMenu();
    },

    // ── Upload ────────────────────────────────────────────────────
    onFileSelect(event) {
      const file = event.target.files[0];
      if (file) {
        this.isUploading = true;
        this.$emit('upload-excel', file);
        this.$refs.fileInput.value = '';
      }
    },
    resetUploadLoading() { this.isUploading = false; },

    // ── Audit log display ─────────────────────────────────────────
    hasLogContent(log) {
      if (!log || !String(log).trim()) return false;
      return String(log).trim() !== '暂无修改记录';
    },
    expandLog(id) { this.$set(this.expandedLogs, id, true); },
    collapseLog(id) { this.$set(this.expandedLogs, id, false); },

    // ── Global click outside handler ──────────────────────────────
    handleClickOutside(event) {
      const inDropdown = event.target.closest('.filter-dropdown, .filter-icon');
      if (!inDropdown) this.closeAllDropdowns();
      const inMenu = event.target.closest('.context-menu');
      if (!inMenu) this.closeContextMenu();
    }
  },
  mounted() {
    document.addEventListener('mousedown', this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.handleClickOutside);
    clearTimeout(this.searchDebounceTimer);
  }
};
</script>

<style scoped>
.data-view-wrapper { position: relative; }

.upload-overlay {
  position: absolute; top: 0; left: 0; width: 100%; height: 100%;
  background-color: rgba(255,255,255,0.75); z-index: 30;
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px;
  color: #2e3968; font-size: 14px;
}
.upload-spinner {
  width: 42px; height: 42px;
  border: 4px solid rgba(23,39,135,0.15); border-top-color: #172787;
  border-radius: 50%; animation: spin 1s linear infinite;
}
.upload-overlay-text { font-weight: 600; letter-spacing: 0.5px; }

.update-time { color: #2e3968; font-size: 14px; margin-bottom: 24px; }

.search-bar { display: flex; justify-content: flex-end; align-items: center; margin-top: 60px; margin-bottom: 12px; gap: 10px; }
.search-bar input { padding: 6px 12px; border: 1px solid #ccc; border-radius: 4px; width: 200px; }
.clear-all-btn { background-color: #f0f0f0; color: #2e3968; border: 1px solid #ccc; padding: 6px 12px; border-radius: 4px; font-size: 13px; cursor: pointer; margin-right: 12px; }
.clear-all-btn:hover { background-color: #2e3968; color: white; border-color: #2e3968; }

.table-wrapper { margin-bottom: 20px; margin-top: 40px; }
.table-shell { border: 1px solid #e8e8e8; border-radius: 6px; background: #fff; }
.table-scroll-body { overflow: auto; max-height: min(92vh, calc(200vh - 260px)); -webkit-overflow-scrolling: touch; }

.data-table { border-collapse: collapse; width: max-content; min-width: 100%; font-size: 14px; white-space: nowrap; }
.data-table th, .data-table td { border: 1px solid #ccc; padding: 6px 10px; text-align: left; color: #2e3968; vertical-align: middle; min-width: 120px; word-break: break-word; white-space: normal; }
.data-table th { background-color: #f4f4f4; position: sticky; top: 0; z-index: 2; box-shadow: 0 1px 0 #ccc; }
.data-table td:first-child, .data-table th:first-child { width: 50px; min-width: 50px; max-width: 60px; text-align: center; }
.data-table td.text-right { text-align: right; }

.th-wrapper { display: flex; align-items: center; justify-content: space-between; position: relative; overflow: visible; z-index: 1; }
.filter-icon { margin-left: 6px; cursor: pointer; font-size: 13px; color: #2e3968; }
.filter-dropdown {
  position: absolute; z-index: 100; background: white; border: 1px solid #ccc;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1); border-radius: 6px; width: 220px;
  max-height: 300px; display: flex; flex-direction: column; top: 100%; left: 0;
}
.filter-search-input { padding: 6px 10px; margin: 8px; font-size: 13px; border: 1px solid #ccc; border-radius: 4px; }
.filter-options { flex: 1; overflow-y: auto; padding: 0 10px 10px; font-size: 13px; display: flex; flex-direction: column; gap: 6px; }
.filter-options label { display: flex; align-items: center; gap: 5px; }
.filter-options input[type="checkbox"] { margin-right: 5px; }
.filter-footer { position: sticky; bottom: 0; background: #f9f9f9; border-top: 1px solid #ddd; padding: 8px 10px; display: flex; justify-content: space-between; gap: 10px; }
.filter-footer button { background-color: #f0f0f0; border: none; padding: 6px 12px; border-radius: 4px; cursor: pointer; font-size: 13px; }
.filter-footer button:hover { background-color: #2e3968; color: white; }

/* ── Inline editing ─────────────────────────────────────────── */
.cell-editable { cursor: text; }
.cell-editable:hover { background-color: #f8f9ff !important; }
.cell-editing { padding: 0 !important; background-color: #fff8e1 !important; }
.cell-input {
  width: 100%; min-height: 34px; padding: 5px 10px;
  border: 2px solid #2e3968; border-radius: 0;
  font-size: 14px; font-family: inherit; color: #2e3968;
  background: white; box-sizing: border-box; outline: none;
}

/* ── Export & upload ─────────────────────────────────────────── */
.export-controls { display: flex; justify-content: flex-end; margin-top: 20px; gap: 10px; }
.export-btn, .upload-btn { background-color: #2e3968; color: white; border: 1px solid transparent; padding: 8px 16px; border-radius: 5px; font-size: 14px; cursor: pointer; transition: all 0.2s ease; }
.export-btn:hover, .upload-btn:hover { background-color: white; color: #2e3968; border: 1px solid #2e3968; }
.upload-btn.is-disabled { opacity: 0.6; cursor: not-allowed; }

/* ── Context menu ────────────────────────────────────────────── */
.context-menu {
  position: fixed; z-index: 1000; background: white;
  border: 1px solid #e0e0e0; border-radius: 6px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.12); min-width: 160px; overflow: hidden;
}
.context-menu-item {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 14px; cursor: pointer; font-size: 13px; transition: background 0.15s;
}
.ctx-delete { color: #ba1a1a; }
.ctx-delete:hover { background: #fff0f0; }
.ctx-icon { font-size: 14px; }
.context-menu-confirm { padding: 10px 14px; }
.ctx-confirm-text { font-size: 12px; color: #444; margin: 0 0 8px; font-weight: 600; }
.ctx-confirm-btns { display: flex; gap: 6px; }
.ctx-btn-confirm { flex: 1; background: #ba1a1a; color: white; border: none; border-radius: 4px; padding: 5px 8px; font-size: 12px; cursor: pointer; }
.ctx-btn-confirm:hover { background: #d32f2f; }
.ctx-btn-cancel { flex: 1; background: #f0f0f0; color: #444; border: 1px solid #d0d0d0; border-radius: 4px; padding: 5px 8px; font-size: 12px; cursor: pointer; }
.ctx-btn-cancel:hover { background: #e0e0e0; }

/* ── Audit log ───────────────────────────────────────────────── */
.modification-log-header { min-width: 280px; width: 320px; background-color: #f4f4f4; font-weight: bold; color: #2e3968; z-index: 3; }
.modification-log-cell { vertical-align: top !important; padding: 10px !important; background-color: #fafafa !important; border-left: 2px solid #e0e0e0 !important; white-space: normal !important; }
.log-content { position: relative; min-width: 260px; }
.log-empty { font-size: 12px; color: #999; }
.log-preview {
  max-height: 2.8em;
  overflow: hidden;
  position: relative;
  white-space: pre-line;
  line-height: 1.4;
  font-size: 12px;
  color: #666;
  word-break: break-word;
  padding-right: 4px;
}
.log-preview::after {
  content: '';
  position: absolute;
  bottom: 0;
  right: 0;
  left: 0;
  height: 1.4em;
  background: linear-gradient(to bottom, rgba(250, 250, 250, 0), #fafafa);
  pointer-events: none;
}
.log-full { white-space: pre-line; line-height: 1.4; font-size: 12px; color: #666; word-break: break-word; }
.log-actions { margin-top: 8px; text-align: right; }
.show-more-btn, .show-less-btn {
  color: #666;
  cursor: pointer;
  font-size: 12px;
  text-decoration: underline;
  background: none;
  border: none;
  padding: 0;
}
.show-more-btn:hover, .show-less-btn:hover { color: #2e3968; }

.no-data { color: #2e3968; margin-top: 20px; text-align: center; padding: 40px; }
:deep(mark) { background-color: yellow; font-weight: bold; padding: 0 2px; border-radius: 2px; }

@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
</style>
